import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { isValidPhone, normalizePhone } from '../utils/phone';
import { validateEmail } from '../utils/validation';
import { ErrorAlert } from '../components/ui/ErrorAlert';

export const ContactUs = () => {
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!phone.trim()) {
      setError('Mobile number is required');
      return;
    }
    if (!isValidPhone(phone)) {
      setError('Enter a valid mobile number');
      return;
    }
    if (email && !validateEmail(email)) {
      setError('Enter a valid email address');
      return;
    }
    if (!message.trim()) {
      setError('Message is required');
      return;
    }

    const normalizedPhone = normalizePhone(phone);
    setIsSubmitting(true);

    try {
      const { error: dbError } = await supabase.from('contact_messages' as any).insert({
        phone_number: normalizedPhone,
        email: email || null,
        message: message.trim(),
      } as any);
      if (dbError) throw dbError;
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = 'w-full rounded border border-gray-300 px-3 py-2 text-sm';

  if (submitted) {
    return (
      <div className="mx-auto max-w-md rounded-lg border border-green-200 bg-green-50 p-6 shadow-sm text-green-800">
        <h1 className="mb-2 text-xl font-bold">Message Sent</h1>
        <p className="mb-4 text-sm">Thank you for contacting us. We will review your message and get back to you soon.</p>
        <Link
          to="/"
          className="inline-block w-full rounded bg-green-600 px-4 py-2 text-center text-sm font-medium text-white hover:bg-green-700"
        >
          Go to Home
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
      <h1 className="mb-2 text-xl font-bold text-gray-900">Contact Us</h1>
      <p className="mb-4 text-sm text-gray-600">Send us your questions or concerns.</p>
      {error && <ErrorAlert message={error} />}
      <form onSubmit={handleSubmit} className="space-y-4">
        <input
          type="tel"
          placeholder="Mobile number *"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className={inputClass}
          required
        />
        <input
          type="email"
          placeholder="Email (optional)"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={inputClass}
        />
        <textarea
          placeholder="Your message *"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className={`${inputClass} min-h-[120px]`}
          maxLength={5000}
          required
        />
        <p className="text-right text-xs text-gray-500">{message.length}/5000</p>
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {isSubmitting ? 'Sending...' : 'Send Message'}
        </button>
      </form>
      <p className="mt-4 text-center text-sm text-gray-600">
        Back to{' '}
        <Link to="/" className="font-medium text-blue-600 hover:text-blue-800">
          Phone Book
        </Link>
      </p>
    </div>
  );
};
